import { useState } from 'react';
import { useFormContext } from 'framework/form';
import useArtifactService from '../../artifactService';
import { CROP_SIZES, ARTIFACT_ID } from '../../constants';
import useArtifactInputHelper from '../ArtifactUploaderInput/artifactsInputHelper';
import useArtifactValidationService from '../ArtifactUploaderInput/artifactValidationService';
import useUploadingQueueService from '../ArtifactUploaderInput/FileUploader/uploadingQueueService';

type ArtifactHelperOptions = {
  artifactTypeId?: string | number;
  artifactsSource?: string;
  isRiskService?: boolean;
};

const useArtifactHelper = ({
  artifactTypeId = ARTIFACT_ID.NOTE_ATTACHMENT,
  artifactsSource,
  isRiskService = false,
}: ArtifactHelperOptions = {}) => {
  const [loadingArtifact, setLoadingArtifact] = useState<Nullable<string>>(null);

  const { setSubmitEnabled = () => {} } = useFormContext();

  const { uploadArtifact, getArtifactsDataByIds, getThumbnail } = useArtifactService({ isRiskService });
  const {
    addArtifactToContext,
    getAcceptExtensions,
    getMaxMb,
    getSource,
    getValue: getArtifacts,
  } = useArtifactInputHelper({
    source: artifactsSource,
    artifactTypeId,
  });

  const { validate: validateArtifact } = useArtifactValidationService({
    previewsCount: getArtifacts() && getArtifacts().length,
    maxMb: getMaxMb(),
    source: getSource(),
    fileTypes: getAcceptExtensions(),
  });

  const { addToUploadingQueue, removeFromUploadingQueue, modifyLoadingProgress } = useUploadingQueueService();

  const handleUploading = async (file: File) => {
    const { temporaryId, signal } = await addToUploadingQueue(file);
    setLoadingArtifact(temporaryId);
    setSubmitEnabled(false);

    const artifactId = await uploadArtifact({
      file,
      artifactTypeId,
      signal,
      onProgress: (loadingProgress: { loaded: any; total: any }) => {
        modifyLoadingProgress(temporaryId, loadingProgress);
      },
    });

    removeFromUploadingQueue(temporaryId);

    const src = artifactId ? await getThumbnail(artifactId, CROP_SIZES.SIZE_100) : null;

    addArtifactToContext(artifactId);
    setLoadingArtifact(null);
    setSubmitEnabled(true);

    return [artifactId, src];
  };

  const cancel = () => {
    if (loadingArtifact) {
      removeFromUploadingQueue(loadingArtifact);
      setLoadingArtifact(null);
      setSubmitEnabled(true);
    }
  };

  const validate = (files: File[]) => {
    return validateArtifact(files);
  };

  return {
    isLoading: loadingArtifact != null,
    uploadArtifact: handleUploading,
    getArtifactsDataByIds,
    getThumbnail,
    cancel,
    validate,
    getArtifacts,
  };
};

export default useArtifactHelper;
